import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { first, catchError, of } from 'rxjs';
import { NotificationService } from './notification/notification.service';

interface VersionResponse {
	hash: string;
}

@Injectable({
	providedIn: 'root',
})
export class VersionCheckService {
	private readonly http = inject(HttpClient);
	private _notificationService = inject(NotificationService);
	private versionCheckInterval?: number;
	private reloadTimeout?: number;
	private currentHash = null;

	/**
	 * Checks in every set frequency the version of frontend application
	 * @param url - The URL to fetch version information from
	 * @param {number} frequency - in milliseconds, defaults to 10 minutes
	 * @returns {void}
	 */
	public initVersionCheck(url: string, frequency = 1000 * 60 * 10): void {
		// Clear any existing interval
		this.stopVersionCheck();

		// Start new interval
		this.versionCheckInterval = window.setInterval(() => {
			this.checkVersion(url);
		}, frequency);

		// Do an initial check
		this.checkVersion(url);
	}

	/**
	 * Stops the version check interval
	 */
	public stopVersionCheck(): void {
		if (this.versionCheckInterval) {
			window.clearInterval(this.versionCheckInterval);
			this.versionCheckInterval = undefined;
		}
		if (this.reloadTimeout) {
			window.clearTimeout(this.reloadTimeout);
			this.reloadTimeout = undefined;
		}
	}

	/**
	 * Will do the call and check if the hash has changed or not
	 * @param url - The URL to fetch version information from
	 * @private
	 */
	private checkVersion(url: string): void {
		const timestamp = new Date().getTime();

		this.http
			.get<VersionResponse>(`${url}?t=${timestamp}`)
			.pipe(
				first(),
				catchError((error) => {
					console.error('Version check failed:', error);
					return of({ hash: this.currentHash });
				})
			)
			.subscribe((response) => {
				const hash = response.hash;
				const hashChanged = this.hasHashChanged(this.currentHash, hash);

				if (hashChanged) {
					this.handleVersionChange();
				}

				this.currentHash = hash;
			});
	}

	/**
	 * Handles version change by showing notification and reloading
	 * @private
	 */
	private handleVersionChange(): void {
		// Clear any existing timeout
		if (this.reloadTimeout) {
			window.clearTimeout(this.reloadTimeout);
		}

		this._notificationService.open({
			title: 'New App Version',
			subtitle:
				'A new version of the app is available. The app will refresh automatically in 1 minute.',
			type: 'info',
			actions: [
				{
					text: 'Refresh Now',
					command: () => {
						window.location.reload();
					},
				},
				{
					text: 'Skip',
					command: () => {
						if (this.reloadTimeout) {
							window.clearTimeout(this.reloadTimeout);
							this.reloadTimeout = undefined;
						}
					},
					variant: 'secondary',
				},
			],
			closeAction: () => {
				if (this.reloadTimeout) {
					window.clearTimeout(this.reloadTimeout);
					this.reloadTimeout = undefined;
				}
			},
		});

		this.reloadTimeout = window.setTimeout(() => {
			window.location.reload();
		}, 1000 * 60 * 1);
	}

	/**
	 * Checks if hash has changed.
	 * @param currentHash - Current application hash
	 * @param newHash - New hash from server
	 * @returns {boolean} - True if hash has changed
	 * @private
	 */
	private hasHashChanged(currentHash: string, newHash: string): boolean {
		// Return false if current hash is null, undefined, or empty
		if (!currentHash?.trim()) {
			return false;
		}

		return currentHash !== newHash;
	}
}
